import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { useSettings } from "../lib/settings";
import { Reveal, Ph } from "../components/Reveal";
import { WhatsAppButton } from "../components/WhatsApp";
import { KATEGORI, KATEGORI_ORDER, KATEGORI_META } from "../lib/utils";
import { Watermark } from "../components/Gunungan";

function InfoRow({ label, value, ph }) {
  return (
    <div className="flex justify-between gap-6 border-b border-zinc-800/70 py-3 text-sm last:border-0">
      <dt className="text-zinc-500">{label}</dt>
      <dd className="text-right font-medium text-zinc-100">
        <Ph value={value}>{ph}</Ph>
      </dd>
    </div>
  );
}

export default function ArmadaKategoriPage() {
  const { key } = useParams();
  const settings = useSettings();
  const valid = !!(key && KATEGORI[key]);
  const [imgIdx, setImgIdx] = useState(0);

  useEffect(() => {
    if (valid) document.title = `${KATEGORI[key]} — Armada Putra Bengawan Logistik`;
    setImgIdx(0);
  }, [key, valid]);

  if (!valid) {
    return (
      <div className="wrap py-32 text-center">
        <p className="eyebrow">404</p>
        <p className="mt-4 font-display text-4xl font-extrabold uppercase text-navy">
          Kategori tidak ditemukan
        </p>
        <Link
          data-testid="kategori-notfound-home"
          to="/"
          className="mt-8 inline-flex rounded-full bg-gold px-7 py-3 font-semibold text-white hover:bg-gold-deep transition-colors"
        >
          Kembali ke Beranda
        </Link>
      </div>
    );
  }

  const meta = KATEGORI_META[key];
  const fotos = meta.fotos || [meta.foto];
  const msg = `Halo Putra Bengawan Logistik, saya ingin menanyakan ketersediaan *${KATEGORI[key]}* untuk kebutuhan pengiriman barang saya.`;
  const others = KATEGORI_ORDER.filter((k) => k !== key);

  return (
    <section className="relative overflow-hidden border-b border-zinc-800">
      <Watermark className="pointer-events-none absolute -right-16 top-8 hidden h-[420px] w-auto text-gold opacity-[0.06] lg:block" />
      <div className="wrap relative py-12 sm:py-16">
        <Link
          data-testid="kategori-back-link"
          to="/"
          className="inline-flex items-center gap-2 text-sm text-zinc-400 transition-colors hover:text-gold"
        >
          <ArrowLeft className="h-4 w-4" /> Kembali ke Beranda
        </Link>
        <div className="mt-8 grid gap-10 lg:grid-cols-5 lg:gap-14">
          <div className="lg:col-span-3">
            <div
              className="overflow-hidden rounded-2xl border border-zinc-800 bg-ink-800"
              data-testid="kategori-detail-photo"
            >
              <img
                src={fotos[imgIdx]}
                alt={KATEGORI[key]}
                className="h-72 w-full object-cover sm:h-[420px]"
              />
            </div>
            {fotos.length > 1 && (
              <div className="mt-4 flex gap-3 overflow-x-auto pb-2">
                {fotos.map((foto, i) => (
                  <button
                    key={i}
                    data-testid={`kategori-thumb-${i}`}
                    onClick={() => setImgIdx(i)}
                    className={`h-20 w-28 shrink-0 overflow-hidden rounded-lg border-2 transition-colors ${
                      i === imgIdx ? "border-gold" : "border-transparent opacity-60 hover:opacity-100"
                    }`}
                  >
                    <img src={foto} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
            <Reveal className="mt-8">
              <h2 className="font-display text-2xl font-extrabold uppercase text-navy">
                Tentang Kategori Ini
              </h2>
              <p className="mt-4 leading-relaxed text-zinc-400">
                {meta.desc}{" "}
                {!meta.jumlah &&
                  "Informasi mengenai dimensi, kapasitas, dan jumlah unit dapat ditanyakan langsung kepada tim kami."}
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-2">
            <p className="eyebrow">Kategori Armada</p>
            <h1
              className="mt-3 font-display text-4xl font-black uppercase leading-none text-navy sm:text-5xl"
              data-testid="kategori-detail-title"
            >
              {KATEGORI[key]}
            </h1>
            <div className="mt-8 rounded-2xl border border-zinc-800 bg-card p-6" data-testid="kategori-detail-specs">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold">
                Informasi Unit
              </p>
              <dl className="mt-4">
                <InfoRow label="Jumlah Unit" value={meta.jumlah} ph="[ Jumlah unit ]" />
                <InfoRow label="Dimensi (P × L × T)" value={meta.dimensi} ph="[ DIMENSI ]" />
                <InfoRow label="Kapasitas Muatan" value={meta.kapasitas} ph="[ KAPASITAS ]" />
              </dl>
              {!meta.jumlah && (
                <p className="mt-4 text-xs leading-relaxed text-zinc-500">
                  Data spesifik unit akan diperbarui. Hubungi kami untuk informasi armada terbaru.
                </p>
              )}
            </div>
            <WhatsAppButton
              settings={settings}
              message={msg}
              testId="kategori-detail-wa"
              variant="wa"
              size="lg"
              className="mt-8 w-full"
            >
              Tanya Armada Ini
            </WhatsAppButton>
            <p className="mt-3 text-center text-xs text-zinc-500">
              Konsultasikan kebutuhan pengiriman Anda — pesan otomatis menyertakan kategori pilihan.
            </p>
          </div>
        </div>
        <div className="mt-16">
          <p className="eyebrow">Kategori Lainnya</p>
          <div className="mt-4 flex flex-wrap gap-3">
            {others.map((k) => (
              <Link
                key={k}
                data-testid={`kategori-lain-${k}`}
                to={`/armada/${k}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-zinc-700 px-5 py-2.5 text-sm font-semibold text-navy transition-colors hover:border-gold hover:text-gold-deep"
              >
                {KATEGORI[k]} <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
